import { getCapNome, getCapNum } from '@/utils/capitulo-fields';

export const READING_TABS_STORAGE_KEY = 'mediocre_reading_tabs';
const MAX_TABS = 6;

export type ReadingTabEntry = {
    obraId: number;
    obraNome: string;
    obraImagem?: string | null;
    capId: number;
    capLabel: string;
    updatedAt: number;
};

function chapterTabLabel(chapter: { id: number; numero?: number | string; nome?: string }): string {
    const nome = getCapNome(chapter as any);
    const num = getCapNum(chapter as any);
    if (nome && String(nome) !== String(num ?? '')) return String(nome);
    if (num != null) return `Cap. ${num}`;
    return 'Capítulo';
}

function loadRaw(): ReadingTabEntry[] {
    if (typeof window === 'undefined') return [];
    try {
        const raw = localStorage.getItem(READING_TABS_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((row): ReadingTabEntry | null => {
                if (row == null || typeof row !== 'object') return null;
                const o = row as Record<string, unknown>;
                const obraId = Number(o.obraId);
                const capId = Number(o.capId);
                if (!Number.isFinite(obraId) || obraId < 1 || !Number.isFinite(capId) || capId < 1) return null;
                return {
                    obraId,
                    obraNome: typeof o.obraNome === 'string' ? o.obraNome : 'Obra',
                    obraImagem: typeof o.obraImagem === 'string' ? o.obraImagem : o.obraImagem === null ? null : undefined,
                    capId,
                    capLabel: typeof o.capLabel === 'string' ? o.capLabel : 'Capítulo',
                    updatedAt: typeof o.updatedAt === 'number' ? o.updatedAt : 0,
                };
            })
            .filter((x): x is ReadingTabEntry => x != null);
    } catch {
        return [];
    }
}

function save(items: ReadingTabEntry[]) {
    if (typeof window === 'undefined') return;
    try {
        localStorage.setItem(READING_TABS_STORAGE_KEY, JSON.stringify(items.slice(0, MAX_TABS)));
    } catch {
        /* ignore */
    }
}

export function emitReadingTabsChanged() {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event('reading-tabs-changed'));
}

/** Últimas obras em leitura (MRU), cada uma com o último capítulo aberto. Máx. 6. */
export function getReadingTabs(): ReadingTabEntry[] {
    return loadRaw();
}

/** Regista leitura deste capítulo: move a obra para o 1.º lugar; se já houver 6 e for obra nova, remove a mais antiga. */
export function touchReadingTab(
    obra: { id: number; nome?: string; imagem?: string | null },
    chapter: { id: number; numero?: number | string; nome?: string },
) {
    if (typeof window === 'undefined') return;
    const list = loadRaw();
    const capLabel = chapterTabLabel(chapter);
    const rest = list.filter((t) => t.obraId !== obra.id);
    const entry: ReadingTabEntry = {
        obraId: obra.id,
        obraNome: String(obra.nome ?? '').trim() || 'Obra',
        obraImagem: obra.imagem ?? null,
        capId: chapter.id,
        capLabel,
        updatedAt: Date.now(),
    };
    const next = [entry, ...rest].slice(0, MAX_TABS);
    save(next);
    emitReadingTabsChanged();
}

export function removeReadingTab(obraId: number) {
    if (typeof window === 'undefined') return;
    const next = loadRaw().filter((t) => t.obraId !== obraId);
    save(next);
    emitReadingTabsChanged();
}
